import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { ArrowUpIcon, ArrowDownIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

function Wallet() {
  const { user } = useAuth();
  const { rewards, claimReward } = useGame();
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    // Combine rewards and user transactions
    const userTransactions = [
      ...rewards.map(reward => ({
        id: reward.id,
        type: 'reward',
        amount: reward.amount,
        date: reward.date,
        status: reward.status,
        description: `Reward from ${reward.type}`
      })),
      ...(user?.transactions || [])
    ];

    // Sort transactions by date
    setTransactions(
      userTransactions.sort((a, b) => new Date(b.date) - new Date(a.date))
    );
  }, [rewards, user?.transactions]);

  const handleClaimReward = async (rewardId) => {
    const result = await claimReward(rewardId);
    if (result.success) {
      toast.success('Reward claimed successfully!');
    } else {
      toast.error(result.error || 'Failed to claim reward');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f56756] to-[#402e2c] p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Disclaimer Banner */}
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 flex items-start gap-3">
          <InformationCircleIcon className="h-5 w-5 text-yellow-400 mt-0.5" />
          <div className="text-yellow-400 text-sm">
            <p className="font-medium">Virtual Currency Notice</p>
            <p>Coins are virtual currency for in-app use only. They have no monetary value and cannot be exchanged for real money or items of value.</p>
          </div>
        </div>

        {/* Balance Card */}
        <div className="bg-[#b34e42]/80 backdrop-blur-sm rounded-2xl p-6 border border-[#402e2c]/20">
          <h2 className="text-xl font-semibold text-white mb-4">Virtual Wallet Balance</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400">Virtual Coins</p>
              <p className="text-3xl font-bold text-white">{user?.coins || 0} coins</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => toast.success('Earn coins by playing games and completing challenges!')}
                className="px-4 py-2 bg-[#402e2c] text-white rounded-full hover:bg-[#402e2c]/80 transition-colors"
              >
                How to Earn
              </button>
            </div>
          </div>
        </div>

        {/* Transactions Section */}
        <div className="bg-[#b34e42]/80 backdrop-blur-sm rounded-2xl p-6 border border-[#402e2c]/20">
          <h2 className="text-xl font-semibold text-white mb-4">Recent Activity</h2>
          <div className="space-y-4">
            {transactions.length > 0 ? (
              transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-4 bg-[#f56756]/50 rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    {transaction.amount > 0 ? (
                      <div className="p-2 bg-green-500/20 rounded-full">
                        <ArrowUpIcon className="h-5 w-5 text-green-400" />
                      </div>
                    ) : (
                      <div className="p-2 bg-red-500/20 rounded-full">
                        <ArrowDownIcon className="h-5 w-5 text-red-400" />
                      </div>
                    )}
                    <div>
                      <p className="text-white font-medium">{transaction.description}</p>
                      <p className="text-gray-400 text-sm">
                        {new Date(transaction.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p
                      className={`font-semibold ${
                        transaction.amount > 0 ? 'text-green-400' : 'text-red-400'
                      }`}
                    >
                      {transaction.amount > 0 ? '+' : ''}
                      {transaction.amount} coins
                    </p>
                    {transaction.type === 'reward' && transaction.status === 'pending' && (
                      <button
                        onClick={() => handleClaimReward(transaction.id)}
                        className="px-3 py-1 bg-[#402e2c] text-white rounded-full text-sm hover:bg-[#402e2c]/80 transition-colors"
                      >
                        Claim
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-400 text-center py-8">No activity yet</p>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-[#b34e42]/80 backdrop-blur-sm rounded-2xl p-6 border border-[#402e2c]/20">
          <h2 className="text-xl font-semibold text-white mb-4">About Virtual Coins</h2>
          <div className="space-y-4 text-gray-400">
            <p>• Virtual coins are earned through gameplay and cannot be purchased</p>
            <p>• Coins can be used to participate in games and tournaments</p>
            <p>• Coins have no monetary value and cannot be exchanged</p>
            <p>• All coin transactions are final and non-refundable</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Wallet; 